import React, { useState, useRef, useEffect, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import loophHoleLogo from '../../assets/logos/horizontalLogoBlue.svg';
import { Outlet } from 'react-router-dom';
import styles from './styles/index.module.css';
import Footer from "../footer/Footer";
import { useAuthStore } from "../../store/authStore";
import { addRole } from "../../services/authService";
import { ApiError } from "../../services/apiClient";

interface NavItem {
  label: string;
  to: string;
}

const Navbar: React.FC = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [roleError, setRoleError] = useState<string>("");
  const [addingRole, setAddingRole] = useState<boolean>(false);

  const session = useAuthStore((s) => s.session);
  const roles = useAuthStore((s) => s.roles);
  const currentRole = useAuthStore((s) => s.currentRole);
  const setCurrentRole = useAuthStore((s) => s.setCurrentRole);
  const refreshRoles = useAuthStore((s) => s.refreshRoles);
  const logout = useAuthStore((s) => s.logout);

  const navigate = useNavigate();

  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const [showMore, setShowMore] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const isAuthed = !!session;
  const isAdmin = currentRole === "admin";
  const isLandlord = currentRole === "landlord" || isAdmin;
  const isTenantOnly = roles.length === 1 && roles[0] === "tenant";

  // Items without a dedicated route yet point at the role dashboard placeholder
  // (real destinations land in phases 03/04).
  const landlordItems: NavItem[] = [
    { label: "Dashboard", to: "/manager/dashboard" },
    { label: "Properties", to: "/properties" },
    { label: "Expenses", to: "/manager/dashboard" },
    { label: "Tenants", to: "/manager/leases" },
  ];
  const tenantItems: NavItem[] = [
    { label: "Dashboard", to: "/tenant/home" },
    { label: "My Home", to: "/tenant/home" },
    { label: "Bookings", to: "/tenant/home" },
    { label: "Maintenance", to: "/tenant/maintenance" },
  ];
  const adminItems: NavItem[] = [
    { label: "Admin", to: "/admin" },
    { label: "Featured", to: "/admin/featured" },
    { label: "Properties", to: "/admin/properties" },
  ];
  const roleItems = isAdmin ? adminItems : isLandlord ? landlordItems : tenantItems;

  const handleSwitchRole = (role: string) => {
    setCurrentRole(role);
    navigate(
      role === "admin"
        ? "/admin"
        : role === "landlord"
          ? "/manager/dashboard"
          : "/tenant/dashboard"
    );
    setShowMobileMenu(false);
  };

  const handleBecomeLandlord = async () => {
    setAddingRole(true);
    setRoleError("");
    try {
      await addRole("landlord");
      await refreshRoles();
      setCurrentRole("landlord");
      navigate("/manager/dashboard");
    } catch (error) {
      setRoleError(error instanceof ApiError ? error.message : "Could not add landlord role.");
    } finally {
      setAddingRole(false);
    }
  };

  const roleLabel = (r: string) =>
    r === "landlord" ? "Landlord" : r === "tenant" ? "Tenant" : r === "admin" ? "Admin" : r;

  useEffect(() => {
    if (!showProfile) return;
    const onDown = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfile(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowProfile(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showProfile]);

  useEffect(() => {
    if (!showMore) return;
    const onDown = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setShowMore(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowMore(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showMore]);

  useEffect(() => {
    if (!showMobileMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowMobileMenu(false);
        hamburgerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showMobileMenu]);

  const first = session?.firstName?.[0] ?? "";
  const last = session?.lastName?.[0] ?? "";
  const fullName =
    [session?.firstName, session?.lastName].filter(Boolean).join(" ") ||
    session?.email ||
    "Account";
  const initials = (first + last || session?.email?.[0] || "U").toUpperCase();
  const profileRole =
    currentRole === "landlord"
      ? "Landlord"
      : currentRole === "tenant"
      ? "Tenant"
      : currentRole || "";

  const handleLogout = () => {
    logout();
    setShowProfile(false);
    setShowMobileMenu(false);
    navigate("/auth");
  };

  const closeMobileMenu = () => setShowMobileMenu(false);

  return (
    <div className={styles.layout}>
      <header className={styles.navBar}>
      <div className={styles.navContainer}>
        <Link to="/">
          <div className={styles.showFullLogo}>
            <img
              alt="loophole logo"
              src={loophHoleLogo}
              className={styles.desktopImageLogo}
              width={170}
              height={40}
            />
          </div>
        </Link>
        <div className={styles.showMobileLogo}>
          <Link to="/" className={styles.mobileLogoLink} onClick={closeMobileMenu} aria-label="Loophole home">
            <img
              alt="loophole logo"
              src={loophHoleLogo}
              className={styles.mobileLogoImg}
              width={130}
              height={30}
            />
          </Link>
          <button
            ref={hamburgerRef}
            type="button"
            className={styles.mobileMenuButton}
            aria-expanded={showMobileMenu}
            aria-controls="mobile-menu"
            aria-label={showMobileMenu ? "Close menu" : "Open menu"}
            onClick={() => {
              setShowMobileMenu((v) => !v);
            }}
          >
            <MenuRoundedIcon
              aria-hidden="true"
              className={`${styles.menuIcon} ${showMobileMenu ? styles.menuIconHidden : ""}`}
            />
            <CloseRoundedIcon
              aria-hidden="true"
              className={`${styles.menuIcon} ${showMobileMenu ? "" : styles.menuIconHidden}`}
            />
          </button>
        </div>

        {showMobileMenu && (
          <nav
            id="mobile-menu"
            aria-label="Mobile"
            className={styles.mobileLinksContainer}
            ref={mobileMenuRef}
          >
            {!isAuthed && (
              <div className={styles.mobileAuthCtas}>
                <Link to="/auth?mode=signup" className={styles.mobileSignupBtn} onClick={closeMobileMenu}>
                  Sign up
                </Link>
                <Link to="/auth?mode=signin" className={styles.mobileLoginBtn} onClick={closeMobileMenu}>
                  Log in
                </Link>
              </div>
            )}
            <Link to="/salesListings" className={styles.navLinks} onClick={closeMobileMenu}>
              Buy
            </Link>
            <Link to="/rentalListings" className={styles.navLinks} onClick={closeMobileMenu}>
              Rent
            </Link>
            <Link to="/contactUs" className={styles.navLinks} onClick={closeMobileMenu}>
              Consult
            </Link>
            <Link to="/roadmap" className={styles.navLinks} onClick={closeMobileMenu}>
              Roadmap
            </Link>
            <Link to="/about" className={styles.navLinks} onClick={closeMobileMenu}>
              About Us
            </Link>
            {isAuthed && (
              <>
                <Link to={roleItems[0].to} className={styles.navLinks} onClick={closeMobileMenu}>
                  {roleItems[0].label}
                </Link>
                <div className={styles.mobileProfile}>
                  <div className={styles.profileName}>{fullName}</div>
                  <div className={styles.profileMeta}>{session?.email}</div>
                  {profileRole && <span className={styles.profileRole}>{profileRole}</span>}
                  {roleItems.slice(1).map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className={styles.menuItem}
                      onClick={() => setShowMobileMenu(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                  {roles.length > 1 && (
                    <div className={styles.menuRoleSwitch}>
                      {roles.map((r) => (
                        <button
                          key={r}
                          type="button"
                          className={r === currentRole ? styles.roleActive : styles.roleOption}
                          aria-pressed={r === currentRole}
                          onClick={() => handleSwitchRole(r)}
                        >
                          {roleLabel(r)}
                        </button>
                      ))}
                    </div>
                  )}
                  {isTenantOnly && (
                    <button
                      type="button"
                      className={styles.becomeLandlord}
                      onClick={handleBecomeLandlord}
                      disabled={addingRole}
                    >
                      {addingRole ? "Working…" : "Become a landlord"}
                    </button>
                  )}
                  {roleError && <span className={styles.navError}>{roleError}</span>}
                  <div className={styles.menuDivider} />
                  <button type="button" className={styles.logoutBtn} onClick={handleLogout}>
                    Log out
                  </button>
                </div>
              </>
            )}
          </nav>
        )}

        <div className={styles.navLeft}>
          <Link to="/salesListings" className={styles.navLinks}>
            Buy
          </Link>
          <Link to="/rentalListings" className={styles.navLinks}>
            Rent
          </Link>
          {isAuthed && (
            <Link to={roleItems[0].to} className={styles.navLinks}>
              {roleItems[0].label}
            </Link>
          )}
        </div>
        <div className={styles.navRight}>
          <div className={styles.moreWrap} ref={moreRef}>
            <button
              type="button"
              className={styles.moreBtn}
              onClick={() => setShowMore((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={showMore}
            >
              More
              <span className={styles.caret} aria-hidden="true">
                ▾
              </span>
            </button>
            {showMore && (
              <div className={styles.moreMenu} role="menu">
                <Link
                  to="/contactUs"
                  className={styles.menuItem}
                  role="menuitem"
                  onClick={() => setShowMore(false)}
                >
                  Consult
                </Link>
                <Link
                  to="/roadmap"
                  className={styles.menuItem}
                  role="menuitem"
                  onClick={() => setShowMore(false)}
                >
                  Roadmap
                </Link>
                <Link
                  to="/about"
                  className={styles.menuItem}
                  role="menuitem"
                  onClick={() => setShowMore(false)}
                >
                  About Us
                </Link>
              </div>
            )}
          </div>
          {!isAuthed && (
            <>
              <Link to="/auth?mode=signin" className={styles.loginLink}>
                Log in
              </Link>
              <Link to="/auth?mode=signup" className={styles.signupBtn}>
                Sign up
              </Link>
            </>
          )}
          {isAuthed && (
            <div className={styles.profileWrap} ref={profileRef}>
              <button
                type="button"
                className={styles.profileBtn}
                onClick={() => setShowProfile((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={showProfile}
              >
                <span className={styles.avatar}>{initials}</span>
                <span className={styles.profileName}>{session?.firstName || "Account"}</span>
              </button>
              {showProfile && (
                <div className={styles.profileMenu} role="menu">
                  <div className={styles.profileHead}>
                    <div className={styles.profileName}>{fullName}</div>
                    <div className={styles.profileMeta}>{session?.email}</div>
                    {profileRole && <span className={styles.profileRole}>{profileRole}</span>}
                  </div>
                  {roleItems.slice(1).map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className={styles.menuItem}
                      role="menuitem"
                      onClick={() => setShowProfile(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                  {roles.length > 1 && (
                    <div className={styles.menuRoleSwitch}>
                      {roles.map((r) => (
                        <button
                          key={r}
                          type="button"
                          className={r === currentRole ? styles.roleActive : styles.roleOption}
                          aria-pressed={r === currentRole}
                          onClick={() => handleSwitchRole(r)}
                        >
                          {roleLabel(r)}
                        </button>
                      ))}
                    </div>
                  )}
                  {isTenantOnly && (
                    <button
                      type="button"
                      className={styles.becomeLandlord}
                      onClick={handleBecomeLandlord}
                      disabled={addingRole}
                    >
                      {addingRole ? "Working…" : "Become a landlord"}
                    </button>
                  )}
                  {roleError && <span className={styles.navError}>{roleError}</span>}
                  <div className={styles.menuDivider} />
                  <button
                    type="button"
                    className={styles.logoutBtn}
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </header>
      {session && !session.confirmedAt && (
        <div className={styles.verifyBanner} role="status">
          Please confirm your email — check your inbox for the confirmation link.
        </div>
      )}
      <Suspense
        fallback={
          <div className={styles.pageLoader} role="status" aria-live="polite">
            Loading…
          </div>
        }
      >
        <main className={styles.main}>
          <Outlet />
        </main>
      </Suspense>
      <Footer />
    </div>
  );
};

export default Navbar;
